import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Switch from "react-switch";
import { useTranslation } from "react-i18next";
import EvidenceModeToggle from "../common/EvidenceModeToggle";
import { categoryColor, categoryMarkStyle } from "../graphlab/graphShared";
import { siteUrl } from "../../utils/constants";

/** The Domain evidence browser as an archive-style view: labeled Select
 *  filters (Collection → Domain → Term → Also mentioning), switch toggles,
 *  then excerpt cards with archived-capture + source links and the
 *  archive's pagination. Data comes precomputed via /api/evidence/*;
 *  counts always match the pipeline's evidence server. Excerpts only — no
 *  whole-article view is served, to avoid republishing copyrighted text.
 *
 *  Renders in two homes: the standalone /evidence page (URL-synced
 *  filters, mode toggle to /evidence/graph) and embedded inside a case
 *  chapter (embedded prop: self-contained state, no URL coupling, the
 *  chapter supplies its own heading and toggle). */

interface SetInfo {
  name: string;
  passages?: number;
}

interface Category {
  id: number;
  label: string;
}

interface TermCount {
  term: string;
  pages: number;
}

interface Hit {
  id: number;
  url: string;
  crawl_date: string;
  snippet: string;
  captures: number;
}

interface ExcerptPage {
  total: number;
  captures: number;
  offset: number;
  pageSize: number;
  wayback: string;
  hits: Hit[];
}

interface Option {
  value: string;
  label: string;
}

/** Card, link and pager styling borrowed from the archive's palette
 *  (_archive.scss): #dee2e6 borders, #990000 accent. */
const evidenceStyles = `
.evd-count { font-size: 15px; color: #757575; text-align: center; }
.evd-hit {
  background: #ffffff; border: 1px solid #dee2e6; border-radius: 4px;
  padding: 20px 25px; margin-bottom: 16px; }
.evd-hit p.snippet { font-size: 16px; line-height: 1.7;
  color: #212529; margin-bottom: 10px; }
.evd-hit mark { background: #990000; color: #ffffff;
  padding: 0 3px; border-radius: 3px; }
.evd-links { font-size: 13px; color: #757575; overflow-wrap: anywhere; }
/* Neutral ink links: inside the cards the category color is the only
   accent — red would read as a second, unrelated accent. */
.evd-links a { color: #212529; text-decoration: underline;
  text-underline-offset: 2px; }
.evd-links button { border: 0; padding: 0; background: none;
  color: #212529; text-decoration: underline; text-underline-offset: 2px;
  cursor: pointer; font-size: 13px; }
.evd-pager { display: flex; flex-wrap: wrap; gap: 5px;
  justify-content: center; align-items: center; }
/* Category options carry the same color dots as the network legend, so
   the palette reads as one identity across both lenses. */
.evd-cat-option { display: inline-flex; align-items: center; gap: 8px; }
.evd-cat-option .swatch { width: 10px; height: 10px; border-radius: 50%;
  flex: none; }
`;

/** Term matches arrive marked with \x01/\x02 sentinels; render them as
 *  <mark> without ever interpreting server text as HTML. markStyle tints
 *  the highlight with the active category's color. */
const Snippet = ({ text, markStyle }: {
  text: string; markStyle?: React.CSSProperties;
}) => (
  <>
    {String(text).split("\x01").map((chunk, i) => {
      if (i === 0) return <React.Fragment key={i}>{chunk}</React.Fragment>;
      const [hit, ...rest] = chunk.split("\x02");
      return (
        <React.Fragment key={i}>
          <mark style={markStyle}>{hit}</mark>
          {rest.join("\x02")}
        </React.Fragment>
      );
    })}
  </>
);

/** First, last, and a window of 2 around the current page; gaps collapse. */
const pageNumbers = (pages: number, current: number): (number | "…")[] => {
  const nums: (number | "…")[] = [];
  for (let i = 0; i < pages; i += 1) {
    if (i === 0 || i === pages - 1 || Math.abs(i - current) <= 2) nums.push(i);
    else if (nums[nums.length - 1] !== "…") nums.push("…");
  }
  return nums;
};

interface EvidenceBrowserProps {
  /** Inside a case chapter: no URL coupling, no page chrome of its own. */
  embedded?: boolean;
  /** Default to the smallest collection (fastest first load) instead of
   *  the first listed. */
  preferSmallestSet?: boolean;
}

const EvidenceBrowser = ({
  embedded = false, preferSmallestSet = false,
}: EvidenceBrowserProps) => {
  const { t } = useTranslation();
  const api = `${siteUrl}/api/evidence`;

  const [sets, setSets] = useState<SetInfo[]>([]);
  const [activeSet, setActiveSet] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [cat, setCat] = useState<number | null>(null);
  const [terms, setTerms] = useState<TermCount[] | null>(null);
  const [term, setTerm] = useState("");
  const [also, setAlso] = useState("");
  const [listings, setListings] = useState(false);
  const [outsidePassages, setOutsidePassages] = useState(false);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<ExcerptPage | null>(null);
  const [countLine, setCountLine] = useState("");
  const [error, setError] = useState("");
  // A slow response must never overwrite the view of a later click.
  const runSeq = useRef(0);
  // Context arriving from the network lens (or a shared deep link):
  // consumed once, when the set it refers to loads. Embedded instances
  // are self-contained and never read the page URL.
  const arrivalRef = useRef<{ set: string | null; cat: string | null;
    term: string | null; also: string | null;
    listings: string | null } | null>(
    (() => {
      if (embedded) return null;
      const params = new URLSearchParams(window.location.search);
      return {
        set: params.get("set"),
        cat: params.get("cat"),
        term: params.get("term"),
        also: params.get("also"),
        listings: params.get("listings"),
      };
    })());

  useEffect(() => {
    axios
      .get(`${api}/sets`)
      .then(({ data }) => {
        setSets(data);
        const wanted = arrivalRef.current?.set;
        if (wanted && data.some((s: SetInfo) => s.name === wanted)) {
          setActiveSet(wanted);
        } else if (data.length) {
          setActiveSet(preferSmallestSet
            ? [...data].sort(
              (a: SetInfo, b: SetInfo) =>
                (a.passages ?? 0) - (b.passages ?? 0))[0].name
            : data[0].name);
        }
      })
      .catch(() =>
        setError(t("evidence.unavailable", "The evidence service is not available.")));
  }, [api, t]);

  useEffect(() => {
    if (!activeSet) return;
    const arrival = arrivalRef.current;
    arrivalRef.current = null;
    setCat(arrival?.cat ? Number(arrival.cat) : null);
    setTerms(null);
    setTerm(arrival?.term || "");
    setAlso(arrival?.also || "");
    if (arrival?.listings === "1") setListings(true);
    setPage(0);
    setResult(null);
    setCountLine("");
    axios.get(`${api}/categories`, { params: { set: activeSet } })
      .then(({ data }) => {
        setCategories(data);
        // Embedded in a chapter: preselect the first domain so the
        // instrument opens showing excerpts instead of an empty state.
        if (embedded && !arrival?.cat && data.length) {
          setCat(data[0].id);
        }
      });
  }, [api, activeSet]);

  useEffect(() => {
    if (!activeSet || cat === null) return;
    setTerms(null);
    axios.get(`${api}/terms`, {
      params: { set: activeSet, category: cat, listings: listings ? 1 : 0 },
    }).then(({ data }) => setTerms(data));
  }, [api, activeSet, cat, listings]);

  useEffect(() => {
    if (!activeSet || cat === null) {
      return;
    }
    const mySeq = runSeq.current + 1;
    runSeq.current = mySeq;
    setCountLine(t("evidence.loading", "loading…"));
    axios.get(`${api}/excerpts`, {
      params: {
        set: activeSet, cat, term, also, page,
        listings: listings ? 1 : 0,
        context: outsidePassages ? 1 : 0,
      },
    }).then(({ data }: { data: ExcerptPage }) => {
      if (runSeq.current !== mySeq) return; // a newer click owns the view
      setResult(data);
      const label = categories.find((c) => c.id === cat)?.label || "";
      const what = term ? `“${term}”` : label;
      const alsoLabel = also
        ? ` ${t("evidence.and", "and")} ${categories.find((c) => c.id === Number(also))?.label || ""}`
        : "";
      const shown = data.hits.length
        ? ` — ${t("evidence.showing", "showing")} ${data.offset + 1}–${data.offset + data.hits.length}`
        : "";
      setCountLine(
        `${data.total.toLocaleString()} ${t("evidence.pagesMention", "pages mention")} ${what}${alsoLabel}`
        + ` (${data.captures.toLocaleString()} ${t("evidence.captures", "captures")})${shown}`);
    });
  }, [api, activeSet, cat, term, also, page, listings, outsidePassages,
      categories, t]);

  const setOptions: Option[] = sets.map(({ name }) => ({
    value: name, label: name,
  }));
  const categoryOptions: Option[] = categories.map(({ id, label }) => ({
    value: String(id), label,
  }));
  const termOptions: Option[] = [
    { value: "", label: t("evidence.entireDomain", "entire domain") },
    ...(terms || []).map(({ term: name, pages }) => ({
      value: name, label: `${name} (${pages.toLocaleString()})`,
    })),
  ];
  const alsoOptions: Option[] = [
    { value: "", label: t("evidence.anyPage", "— any page —") },
    ...categoryOptions,
  ];

  // Category entries show the network legend's color dot; entries with
  // no category (e.g. "any page") render plain.
  const categoryOptionLabel = (option: Option) => (
    option.value ? (
      <span className="evd-cat-option">
        <span className="swatch"
          style={{ background: categoryColor(Number(option.value)) }} />
        {option.label}
      </span>
    ) : option.label
  );

  const renderFilter = (
    label: string, id: string, options: Option[], value: Option | null,
    onChange: (option: Option | null) => void, disabled = false,
    placeholder?: string,
    formatOptionLabel?: (option: Option) => React.ReactNode,
  ) => (
    // The plain wrapper div matters: .filter-item is width:100%, so as a
    // direct flex child it would take a full row — the archive's
    // FilterSection wraps each one the same way to get the centered grid.
    <div key={id}>
      <div className="filter-item mt-xl-4">
        <label htmlFor={id} className="mr-2 section-title">
          <span className="subtitle">{label}</span>
        </label>
        <div className="select__container">
          <Select
            inputId={id}
            options={options}
            value={value}
            onChange={onChange}
            isDisabled={disabled}
            placeholder={placeholder || "Select..."}
            formatOptionLabel={formatOptionLabel}
            className="basic-single"
            classNamePrefix="select"
          />
        </div>
      </div>
    </div>
  );

  const pagInt = result ? Math.ceil(result.total / result.pageSize) : 0;

  // The current filters live in the URL: a reload restores them, and the
  // mode toggle (or copy-pasting the address) carries them along.
  // Embedded instances leave the case page's URL alone.
  useEffect(() => {
    if (embedded || !activeSet) return;
    const params = new URLSearchParams();
    params.set("set", activeSet);
    if (cat !== null) params.set("cat", String(cat));
    if (term) params.set("term", term);
    if (also) params.set("also", also);
    if (listings) params.set("listings", "1");
    window.history.replaceState(
      null, "", `${window.location.pathname}?${params.toString()}`);
  }, [embedded, activeSet, cat, term, also, listings]);

  // Flip to the network lens keeping everything that has an equivalent
  // there: collection, term (pinned), a term-less category (focused via
  // the legend), and the listings toggle.
  const toGraph = (() => {
    const params = new URLSearchParams();
    if (activeSet) params.set("set", activeSet);
    if (term) params.set("term", term);
    else if (cat !== null) params.set("cat", String(cat));
    if (listings) params.set("listings", "1");
    const query = params.toString();
    return `/evidence/graph${query ? `?${query}` : ""}`;
  })();

  return (
    <>
      <style>{evidenceStyles}</style>
      {!embedded && (
        <EvidenceModeToggle
          mode="search"
          toSearch="/evidence"
          toGraph={toGraph}
        />
      )}
      {!embedded && (
        <div className="row">
          <div className="col-lg-12">
            <div className="section-title mb--30 text-center">
              <h3 className="title mt--20 mb--50">
                {t("evidence.searchBy", "Explore the evidence:")}
              </h3>
            </div>
          </div>
        </div>
      )}

      {error && <p className="evd-count">{error}</p>}

      {!error && (
        <>
          <div className="col-lg-12 d-flex flex-wrap justify-content-center filter-container mb--30">
            {renderFilter(
              t("evidence.collection", "Collection"), "evidence-set",
              setOptions,
              setOptions.find((o) => o.value === activeSet) || null,
              (option) => option && setActiveSet(option.value))}
            {renderFilter(
              t("evidence.domain", "Domain"), "evidence-domain",
              categoryOptions,
              categoryOptions.find((o) => Number(o.value) === cat) || null,
              (option) => {
                if (!option) return;
                setCat(Number(option.value));
                setTerm("");
                setPage(0);
              }, false, undefined, categoryOptionLabel)}
            {renderFilter(
              t("evidence.term", "Term"), "evidence-term",
              termOptions,
              cat === null ? null
                : termOptions.find((o) => o.value === term) || null,
              (option) => {
                setTerm(option ? option.value : "");
                setPage(0);
              },
              cat === null || terms === null,
              cat === null
                ? t("evidence.pickDomain", "Pick a domain first...")
                : terms === null
                  ? t("evidence.loadingTerms", "loading terms…")
                  : "Select...")}
            {renderFilter(
              t("evidence.alsoMentioning", "Also mentioning"),
              "evidence-also", alsoOptions,
              alsoOptions.find((o) => o.value === also) || alsoOptions[0],
              (option) => {
                setAlso(option ? option.value : "");
                setPage(0);
              },
              cat === null, undefined, categoryOptionLabel)}
          </div>

          {cat !== null && (
            <div className="switch-container text-center mt-xl-4 mb-xl-4 d-flex justify-content-center align-items-center flex-wrap" style={{ gap: "28px" }}>
              <label className="d-flex align-items-center" style={{ gap: "10px", marginBottom: 0, cursor: "pointer" }}>
                <span>{t("evidence.listings", "Include listings/directories")}</span>
                <Switch
                  checked={listings}
                  onChange={() => { setListings(!listings); setPage(0); }}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#990000"
                  height={22}
                  width={44}
                />
              </label>
              <label className="d-flex align-items-center" style={{ gap: "10px", marginBottom: 0, cursor: "pointer" }}>
                <span>{t("evidence.outsidePassages", "Include occurrences outside activism passages")}</span>
                <Switch
                  checked={outsidePassages}
                  onChange={() => { setOutsidePassages(!outsidePassages); setPage(0); }}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#990000"
                  height={22}
                  width={44}
                />
              </label>
            </div>
          )}

          {cat !== null && <p className="evd-count mb--30">{countLine}</p>}

          <div className="row">
            <div className="col-lg-10 offset-lg-1 col-12">
              {result && result.hits.map((hit) => (
                <div key={`${hit.id}-${hit.url}`} className="evd-hit">
                  <p className="snippet">
                    <Snippet text={hit.snippet}
                      markStyle={cat !== null
                        ? categoryMarkStyle(cat) : undefined} />
                  </p>
                  <div className="evd-links">
                    <a
                      href={`${result.wayback}${hit.crawl_date || "*"}/${hit.url}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("evidence.capture", "original as crawled")}
                    </a>
                    {" · "}
                    <a
                      href={hit.url}
                      target="_blank"
                      rel="noreferrer"
                      title={hit.url}
                    >
                      {t("evidence.source", "source page")}
                    </a>
                    {/* No in-app full-text view on purpose: reproducing
                        whole copyrighted articles isn't published from
                        our server. The excerpt plus the two links above
                        (archived capture + source) is the public view. */}
                    {hit.captures > 1 && (
                      <span> · {hit.captures} {t("evidence.captures", "captures")}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {pagInt > 1 && result && (
            <div className="evd-pager mt--40">
              <button
                type="button"
                className="pagination-button prev"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                <span className="pagination-arrow">‹</span>
              </button>
              {pageNumbers(pagInt, page).map((n, i) =>
                n === "…" ? (
                  <span key={`gap-${i}`} className="pagination-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    className={`pagination-button${n === page ? " active" : ""}`}
                    style={n === page && cat !== null
                      ? categoryMarkStyle(cat) : undefined}
                    onClick={() => setPage(n)}
                  >
                    {n + 1}
                  </button>
                ))}
              <button
                type="button"
                className="pagination-button next"
                disabled={result.offset + result.hits.length >= result.total}
                onClick={() => setPage(page + 1)}
              >
                <span className="pagination-arrow">›</span>
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default EvidenceBrowser;
